import {
    Button,
    Collapse,
    ColorPicker,
    Divider,
    Empty,
    Form,
    Modal,
    Popover,
    Segmented,
    Select,
    Space,
    theme,
} from "antd";
import { startCase } from "lodash";
import React, { useState } from "react";
import styles from "./styles/GSBox.module.css";
import {
    FormatPainterOutlined,
    UndoOutlined,
    CheckOutlined,
} from "@ant-design/icons";
import log from "cslog";
import { ThemeGeneratorCore } from "../../sandbox/ThemeGenerator";

export function GSModal({
    values = {},
    onChange = () => {},
    initial_values,
    org = {},
}) {
    const [open, setOpen] = useState(false);

    const init_config = initial_values?.config || {};
    const config = values?.config || {};

    log.d(org, "org in GSBox");

    return (
        <>
            {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
            <Popover
                content={
                    <div className={styles.wrapper}>
                        <GSBox
                            values={values}
                            onChange={onChange}
                            initial_values={initial_values}
                            org={org}
                        />
                    </div>
                }
                title="Theme Config"
                trigger="click"
                open={open}
                onOpenChange={setOpen}
                placement="bottomLeft"
            >
                <Button
                    icon={<FormatPainterOutlined />}
                    style={{ marginTop: "10px" }}
                >
                    Theme Settings
                </Button>
            </Popover>
        </>
    );
}

function GSBox({
    values = {},
    onChange = () => {},
    initial_values,
    org = {},
    design_theme,
}) {
    const init_config = initial_values?.config || {};
    const config = values?.config || {};

    log.d(org, "org in GSBox");

    return (
        <div className={styles.gsbox_root}>
            <div className={styles.section_label}>Active Palette</div>
            <ThemeColorPicker
                value={config}
                initial_theme={init_config}
                onChange={(new_val) => {
                    onChange({
                        ...values,
                        config: new_val,
                    });
                }}
            />

            <div className={styles.section_label} style={{ marginTop: 14 }}>
                Palette Library
            </div>
            <ThemeDropdrown
                color_palettes={org?.color_palettes || []}
                theme={config}
                onChange={(new_val) => {
                    onChange({
                        ...values,
                        config: new_val,
                    });
                }}
                initial_theme={init_config}
                design_theme={design_theme}
            />

            <div className={styles.action_bar}>
                <Button
                    icon={<UndoOutlined />}
                    size="small"
                    onClick={() => onChange({ config: {} })}
                >
                    Reset
                </Button>
            </div>
        </div>
    );
}

export default GSBox;

export function ThemeFontsBox({
    values = {},
    onChange = () => {},
    config = {},
}) {
    return (
        <Space direction="horizontal">
            {["primaryFont", "secondaryFont"].map((item) => (
                <Form.Item label={startCase(item)}>
                    <Select
                        defaultValue={config[item]}
                        onChange={(value) => {
                            onChange({
                                ...values,
                                config: {
                                    ...config,
                                    [item]: value,
                                },
                            });
                        }}
                        style={{ width: 180 }}
                        options={[
                            {
                                value: "serif",
                                label: "Serif",
                            },
                            {
                                value: "sans-serif",
                                label: "Sans Serif",
                            },
                            {
                                value: "monospace",
                                label: "Monospace",
                            },
                            {
                                value: "cursive",
                                label: "Cursive",
                            },
                            {
                                value: "fantasy",
                                label: "Fantasy",
                            },
                        ]}
                    />
                </Form.Item>
            ))}
        </Space>
    );
}

export function ThemeGenerateModal({
    trigger = <Button>Generate</Button>,
    value = {},
    onChange = () => {},
    onChangeThemes = () => {},
}) {
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState(null);
    const [themes, setThemes] = useState([]);

    return (
        <>
            <Space>
                <span onClick={() => setOpen(true)}>{trigger}</span>
                <Button>Generate from Logo</Button>
            </Space>

            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                title="Generate Theme Palette"
                okText="Okay"
                width={650}
                style={{
                    top: 40, // Fixed position from top
                }}
                styles={{
                    body: {
                        height: "60vh",
                        overflowY: "auto",
                        paddingRight: 10,
                    },
                }}
                onOk={() => {
                    onChange(theme);
                    onChangeThemes(themes);
                    setOpen(false);
                }}
            >
                <Form layout="vertical">
                    <ThemeGeneratorCore
                        setTheme={(new_val) => setTheme(new_val)}
                        setThemes={(new_val) => {
                            setThemes(new_val);
                            onChangeThemes(new_val);
                        }}
                    />

                    {theme && isValidTheme(theme) && (
                        <ThemeColorPicker
                            value={theme}
                            initial_theme={theme}
                            onChange={setTheme}
                        />
                    )}

                    {themes?.length > 0 && (
                        <Collapse
                            size="large"
                            items={[
                                {
                                    key: "1",
                                    label: "Extended Palettes",
                                    children: (
                                        <div
                                            id="extended-palettes"
                                            style={{
                                                maxHeight: "200px",
                                                overflowY: "auto",
                                                paddingRight: "6px",
                                            }}
                                        >
                                            {themes.map((item) => (
                                                <ThemeColorPicker
                                                    key={item.id}
                                                    value={item}
                                                    onChange={onChange}
                                                    show_label={false}
                                                    size="small"
                                                    selectable={true}
                                                />
                                            ))}
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    )}
                </Form>
            </Modal>
        </>
    );
}
export function ThemeColorPicker({
    value: theme,
    initial_theme = {},
    onChange,
    show_label = true,
    size = "normal",
    selectable = false,
    isSelected = false,
}) {
    const setColor = (key, color) => {
        onChange({ ...theme, [key]: `#${color.toHex()}` });
    };

    const COLOR_GROUPS = [
        ["Primary", ["primaryColor", "primaryColor2"]],
        ["Secondary", ["secondryColor", "accentColor"]],
        ["Neutral", ["neutralColor", "neutralColor2"]],
    ];

    // ── Selectable mode: compact horizontal swatch card ──
    if (selectable) {
        const colorValues = COLOR_GROUPS.flatMap(([, keys]) =>
            keys.map((k) => theme[k] || initial_theme[k] || "#ccc")
        );

        return (
            <div
                className={`${styles.palette_card} ${
                    isSelected ? styles.palette_card_selected : ""
                }`}
                onClick={() => onChange({ ...theme })}
            >
                <div className={styles.palette_swatch_row}>
                    {colorValues.map((color, i) => (
                        <div
                            key={i}
                            className={styles.palette_swatch_segment}
                            style={{ backgroundColor: color }}
                        />
                    ))}
                </div>
                {isSelected ? (
                    <div className={styles.palette_select_ring}>
                        <CheckOutlined style={{ fontSize: "9px" }} />
                    </div>
                ) : (
                    <div className={styles.palette_select_ring_empty} />
                )}
            </div>
        );
    }

    // ── Editor mode: labeled groups with clickable swatches ──
    return (
        <div className={styles.active_palette_row}>
            {COLOR_GROUPS.map(([label, keys]) => (
                <div key={label} className={styles.active_palette_group}>
                    {show_label && (
                        <span className={styles.palette_group_label}>
                            {label}
                        </span>
                    )}
                    <div className={styles.active_swatches}>
                        {keys.map((key) => (
                            <ColorPicker
                                key={key}
                                value={theme[key]}
                                onChange={(color) => setColor(key, color)}
                            >
                                <div
                                    className={styles.active_swatch}
                                    style={{
                                        backgroundColor:
                                            theme[key] || initial_theme[key],
                                    }}
                                />
                            </ColorPicker>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function ThemeDropdrown({
    color_palettes = [],
    theme,
    onChange,
    initial_theme = {},
    design_theme,
}) {
    log.d(theme, "theme in GSBox");
    const [selected_palettes, setSelectedPalettes] = useState("Generate");
    const [generated_palettes, setGeneratedPalettes] = useState([]);

    const is_brand_palette_available = color_palettes?.length > 0;

    const palette_options =
        selected_palettes === "Brand Colors"
            ? color_palettes
            : selected_palettes === "Standard Colors"
            ? STANDARD_PALETTES
            : selected_palettes === "Generate"
            ? generated_palettes
            : [];

    return (
        <div>
            <div className={styles.source_tabs_wrapper}>
                <Segmented
                    options={["Brand Colors", "Standard Colors", "Generate"]}
                    value={selected_palettes}
                    onChange={(new_val) => setSelectedPalettes(new_val)}
                    block
                />
            </div>

            {selected_palettes === "Generate" && (
                <ThemeGeneratorCore
                    setTheme={(new_val) => {
                        onChange(new_val);
                    }}
                    setThemes={(new_val) => {
                        if (design_theme?.startsWith("force_")) {
                            if (design_theme === "force_light") {
                                setGeneratedPalettes(
                                    new_val?.filter(
                                        (item) => item?.theme === "light"
                                    )
                                );
                            } else if (design_theme === "force_dark") {
                                setGeneratedPalettes(
                                    new_val?.filter(
                                        (item) => item?.theme === "dark"
                                    )
                                );
                            }
                        } else {
                            setGeneratedPalettes(new_val);
                        }
                    }}
                />
            )}

            {palette_options?.length === 0 &&
                selected_palettes !== "Generate" && (
                    <Empty description="No brand colors setup">
                        <Button type="primary">Open Brand Settings</Button>
                    </Empty>
                )}

            {palette_options?.length > 0 && (
                <div className={styles.palettes_list}>
                    {palette_options.map((item, index) => {
                        log.d(item, "item in GSBox");
                        return (
                            <ThemeColorPicker
                                // key={item.id || item.name}
                                key={index}
                                value={item}
                                initial_theme={initial_theme}
                                onChange={(new_val) => onChange(new_val)}
                                show_label={false}
                                size="small"
                                selectable={true}
                                isSelected={isThemeEqual(item, theme)}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function isThemeEqual(theme1, theme2) {
    return (
        theme1.primaryColor === theme2.primaryColor &&
        theme1.primaryColor2 === theme2.primaryColor2 &&
        theme1.secondryColor === theme2.secondryColor &&
        theme1.accentColor === theme2.accentColor &&
        theme1.neutralColor === theme2.neutralColor &&
        theme1.neutralColor2 === theme2.neutralColor2
    );
}
function isValidTheme(theme) {
    const requiredKeys = [
        "primaryColor",
        "primaryColor2",
        "secondryColor",
        "accentColor",
        "neutralColor",
        "neutralColor2",
    ];
    return requiredKeys.every((key) => theme.hasOwnProperty(key));
}

const STANDARD_PALETTES = [
    {
        name: "sunset_bloom",
        secondryColor: "#FF6B6B",
        primaryColor: "#FDFDFD",
        primaryColor2: "#FFE3E3",
        neutralColor: "#1A1A1A",
        neutralColor2: "#8A8A8A",
        accentColor: "#3D348B",
        rank: 1,
        theme: "light",
    },
    {
        name: "forest_glow",
        secondryColor: "#2E7D32",
        primaryColor: "#FAFAFA",
        primaryColor2: "#E6F4E6",
        neutralColor: "#111111",
        neutralColor2: "#7A7A7A",
        accentColor: "#FFB300",
        rank: 2,
        theme: "light",
    },
    {
        name: "royal_mystic",
        secondryColor: "#673AB7",
        primaryColor: "#F7F7FF",
        primaryColor2: "#E8E0F9",
        neutralColor: "#000000",
        neutralColor2: "#999999",
        accentColor: "#FF9800",
        rank: 3,
        theme: "light",
    },
    {
        name: "aqua_wave",
        secondryColor: "#0097A7",
        primaryColor: "#F9FFFF",
        primaryColor2: "#D6F4F4",
        neutralColor: "#0F0F0F",
        neutralColor2: "#909090",
        accentColor: "#F06292",
        rank: 4,
        theme: "light",
    },
    {
        name: "sunflower_energy",
        secondryColor: "#FFC107",
        primaryColor: "#1C1C1C",
        primaryColor2: "#2A2A2A",
        neutralColor: "#F5F5F5",
        neutralColor2: "#B0B0B0",
        accentColor: "#3F51B5",
        rank: 5,
        theme: "dark",
    },
    {
        name: "ocean_midnight",
        secondryColor: "#1565C0",
        primaryColor: "#FDFEFF",
        primaryColor2: "#E1EAF9",
        neutralColor: "#0B0B0B",
        neutralColor2: "#868686",
        accentColor: "#FF7043",
        rank: 6,
        theme: "light",
    },
    {
        name: "desert_dusk",
        secondryColor: "#D84315",
        primaryColor: "#FEFEFE",
        primaryColor2: "#F6E2DA",
        neutralColor: "#101010",
        neutralColor2: "#7E7E7E",
        accentColor: "#4CAF50",
        rank: 7,
        theme: "light",
    },
    {
        name: "peachy_mint",
        secondryColor: "#FF7043",
        primaryColor: "#FBFBFB",
        primaryColor2: "#FFE8E1",
        neutralColor: "#0D0D0D",
        neutralColor2: "#9C9C9C",
        accentColor: "#4DB6AC",
        rank: 8,
        theme: "light",
    },
    {
        name: "lavender_night",
        secondryColor: "#9C27B0",
        primaryColor: "#FFFFFF",
        primaryColor2: "#F3E5F5",
        neutralColor: "#000000",
        neutralColor2: "#868686",
        accentColor: "#FF5722",
        rank: 9,
        theme: "light",
    },
    {
        name: "cyber_neon",
        secondryColor: "#00E5FF",
        primaryColor: "#121212",
        primaryColor2: "#1E1E1E",
        neutralColor: "#F5F5F5",
        neutralColor2: "#B8B8B8",
        accentColor: "#FF00A0",
        rank: 10,
        theme: "dark",
    },
];
